import { useForm, Controller, type SubmitHandler } from "react-hook-form"

import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import { UserRole, type UserRoleFormInput, type User } from "@/types"
import { useUpdateUserRole } from "@/hooks/api/use-users"
import { USER_ROLE_LABEL_MAP } from "@/constants/general"
import { getUserRole } from "@/helpers"
import MunicipalitiesLoader from "@/wrappers/municipalities-loader"
import CollectionCompaniesLoader from "@/wrappers/collection-companies-loader"

const userRole = getUserRole()

const tieredRoles = {
    [UserRole.admin]: [
        UserRole.admin,
        UserRole.collectionCompany,
        UserRole.municipalityOfficial,
    ],
    [UserRole.collectionCompany]: [
        UserRole.collectionCompany,
        UserRole.municipalityOfficial,
    ],
    [UserRole.eokoDataAdmin]: [
        UserRole.collectionCompany,
        UserRole.municipalityOfficial,
        UserRole.eokoDataAdmin,
    ],
    [UserRole.municipalityOfficial]: [UserRole.municipalityOfficial],
    [UserRole.basic]: [],
    [UserRole.collectionPointAdmin]: [],
    [UserRole.resident]: [],
}

const UserRoleForm = ({
    user,
    closeCallback,
} : {
    user: User,
    closeCallback?: () => void,
}) => {

    const initialValues = {
        role: user.role ?? UserRole.basic,
        municipality_id: user.municipality?.id ?? '',
        collection_company_id: user.collection_company?.id ?? '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        watch,
        setValue,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        error,
        setError,
    } = useFormStates(() => reset(initialValues))

    const mutationCreate = useUpdateUserRole(
        user.id,
        {
            setRequestInProgress,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        }
    )

    const onSubmit: SubmitHandler<UserRoleFormInput> = data => {
        setRequestInProgress(true)
        mutationCreate.mutate(data)
    }

    const filledRole = watch('role')
    const filledMunicipalityId = watch('municipality_id')
    const filledCollectionCompanyId = watch('collection_company_id')

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={'Upravit roli a příslušnost'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={false}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            error={error}
            setError={setError}
            disableSave={
                user.role === filledRole &&
                (user.municipality?.id ?? '') === filledMunicipalityId &&
                (user.collection_company?.id ?? '') === filledCollectionCompanyId
            }
        >
            <FormControl fullWidth>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Rok">
                            {Array.from(new Set([...tieredRoles[userRole], user.role])).map(role => role !== UserRole.collectionPointAdmin &&(
                                <MenuItem
                                    key={role}
                                    value={role}
                                >
                                    {USER_ROLE_LABEL_MAP[role]}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            {(filledRole === UserRole.collectionCompany || filledRole === UserRole.municipalityOfficial) &&
                <CollectionCompaniesLoader>
                    {collectionCompanies => (
                        <FormControl fullWidth>
                            <InputLabel id="municipality-select-label">Svozová společnost</InputLabel>
                            <Controller
                                name="collection_company_id"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="Svozová společnost"
                                        onChange={(e) => {
                                            field.onChange(e)
                                            setValue("municipality_id", '')
                                        }}
                                    >
                                        <MenuItem key="none" value="">-</MenuItem>
                                        {collectionCompanies.map(collectionCompany => (
                                            <MenuItem key={collectionCompany.id} value={collectionCompany.id}>{collectionCompany.eoko_customer?.name}</MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    )}
                </CollectionCompaniesLoader>
            }
            {(filledRole === UserRole.collectionCompany || filledRole === UserRole.municipalityOfficial) &&
                <MunicipalitiesLoader collectionCompanyId={filledCollectionCompanyId.toString()}>
                    {municipalities => (
                        <FormControl fullWidth>
                            <InputLabel id="municipality-select-label">Obec</InputLabel>
                            <Controller
                                name="municipality_id"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} label="Obec">
                                        <MenuItem key="none" value="">-</MenuItem>
                                        {municipalities.map(municipality => (
                                            <MenuItem key={municipality.id} value={municipality.id}>{municipality.name}</MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    )}
                </MunicipalitiesLoader>
            }
        </Form>
    )
}

export default UserRoleForm
